import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, MessageSquareText, ShieldCheck, CreditCard, Phone, RotateCcw, ImagePlus, X, Plus } from "lucide-react";
import { Eyebrow, Reveal, EMAIL, PHONE, PHONE_RAW, estimateMulti } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-stone-700 bg-[#0c0a09] px-4 py-3 text-base sm:text-sm text-bone placeholder:text-stone-600 focus:border-amber-500 focus:outline-none transition-colors duration-150";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";

function buildBody(form, widths, photoUrls = []) {
  const { low, high } = estimateMulti(widths);
  return [
    `Name: ${form.name}`,
    `Phone: ${form.phone}`,
    form.email ? `Email: ${form.email}` : null,
    `Address: ${form.address}`,
    `Stump sizes (flare roots, in): ${widths.join(" + ")}`,
    `Number of stumps: ${widths.length}`,
    form.hasGate === "yes"
      ? `Gate: yes — tightest opening ${form.gateOpening || "?"} inches`
      : form.hasGate === "no"
        ? "Gate: no gate / open access"
        : null,
    ...photoUrls.map((u, i) => `Stump photo ${i + 1}: ${u}`),
    `Site estimate: $${low}–$${high}`,
    form.notes ? `Notes: ${form.notes}` : null,
    "",
    "— sent from FridayStumps.com",
  ]
    .filter(Boolean)
    .join("\n");
}

export default function QuoteForm({ widths, setWidths }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "", hasGate: "", gateOpening: "" });
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentSmsHref, setSentSmsHref] = useState("");
  const MAX_PHOTOS = 4;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setW = (i) => (e) =>
    setWidths(widths.map((w, j) => (j === i ? e.target.value : w)));
  const clampW = (i) => () =>
    setWidths((cur) =>
      cur.map((w, j) =>
        j === i ? Math.max(1, Math.min(120, Number(w) || 12)) : w,
      ),
    );
  const addStump = () => setWidths((cur) => (cur.length < 6 ? [...cur, 12] : cur));
  const removeStump = (i) => setWidths((cur) => cur.filter((_, j) => j !== i));

  const validWidths = widths.map(Number).filter((n) => n >= 1);
  const liveEst = validWidths.length ? estimateMulti(validWidths) : null;

  const onPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const accepted = [];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" is over 10MB — skipped.`);
        continue;
      }
      accepted.push({
        file,
        preview: URL.createObjectURL(file),
        id: `${file.name}-${file.size}-${file.lastModified}`,
      });
    }
    setPhotos((cur) => {
      const room = MAX_PHOTOS - cur.length;
      if (accepted.length > room) toast.warning(`Up to ${MAX_PHOTOS} photos — extra ones skipped.`);
      return [...cur, ...accepted.slice(0, Math.max(0, room))];
    });
  };

  const removePhoto = (id) => setPhotos((cur) => cur.filter((p) => p.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const nums = widths.map((w) => Math.max(1, Math.min(120, Number(w) || 12)));
    const uploadedPaths = [];
    for (const p of photos) {
      try {
        const fd = new FormData();
        fd.append("file", p.file);
        const up = await axios.post(`${API}/uploads`, fd);
        uploadedPaths.push(up.data.path);
      } catch {
        toast.warning(`"${p.file.name}" didn't upload — continuing without it.`);
      }
    }
    try {
      const { low, high } = estimateMulti(nums);
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        address: form.address.trim(),
        stump_width_in: nums[0],
        num_stumps: nums.length,
        extra_stump_widths: nums.slice(1),
        notes: form.notes.trim() || null,
        estimate_low: low,
        estimate_high: high,
        has_gate: form.hasGate === "yes",
        gate_opening_in: form.hasGate === "yes" && form.gateOpening ? Number(form.gateOpening) : null,
        photo_paths: uploadedPaths,
      });
    } catch {
      toast.error("Something glitched — please text Eric directly at (614) 325-1273.");
      setSending(false);
      return;
    }
    const photoUrls = uploadedPaths.map(
      (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`,
    );
    const body = buildBody(form, nums, photoUrls);
    setSentSmsHref(`sms:${PHONE_RAW}?&body=${encodeURIComponent(body)}`);
    toast.success("Request sent — Eric has been emailed.");
    setSending(false);
    setSent(true);
  };

  const smsHref = sentSmsHref || `sms:${PHONE_RAW}`;

  return (
    <section id="quote" data-testid="quote-form-section" className="mx-auto max-w-4xl scroll-mt-20 px-4 py-20 sm:px-8 sm:py-28">
      <Reveal>
        <Eyebrow>05 — Instant Quote</Eyebrow>
        <h2 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-bone sm:text-3xl lg:text-4xl">
          Get on Friday's route.
        </h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-400 sm:text-base">
          Fill this out and it goes straight to {EMAIL} — or skip the form
          entirely and text a photo to {PHONE}. Either way, you get a firm
          flat price back by text.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        {sent ? (
          <div data-testid="quote-success-panel" className="mt-10 border border-green-700/50 bg-green-500/5 p-8 sm:p-10">
            <p className="font-display text-2xl font-black uppercase text-bone sm:text-3xl">
              Request sent. <span className="text-amber-500">Nice.</span>
            </p>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone-400 sm:text-base">
              Eric just got an email with your details and photo — he texts
              you a firm, flat quote, usually within hours. Once you approve,
              he calls 811 to mark utility lines (takes up to 3 days — late-week
              approvals may roll to the following Friday), then grind day:
              leave the gate unlocked and it's gone. Prefer to nudge him yourself?
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                data-testid="quote-sms-fallback"
                href={smsHref}
                className="flex items-center gap-2 bg-amber-500 px-5 py-3 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400"
              >
                <MessageSquareText className="h-4 w-4" />
                Send as SMS to {PHONE}
              </a>
              <button
                data-testid="quote-reset-btn"
                onClick={() => setSent(false)}
                className="flex items-center gap-2 border border-stone-700 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Submit another
              </button>
            </div>
          </div>
        ) : (
          <form data-testid="quote-form" onSubmit={submit} className="mt-10 border border-stone-800 bg-charcoal p-6 sm:p-10">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="q-name">Full name *</label>
                <input id="q-name" data-testid="quote-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Buckeye" />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-phone">Phone / SMS *</label>
                <input id="q-phone" data-testid="quote-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="614-555-0139" />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-email">Email (optional — instant confirmation)</label>
                <input id="q-email" data-testid="quote-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="q-address">Street address & zip *</label>
                <input id="q-address" data-testid="quote-address-input" required className={inputCls} value={form.address} onChange={set("address")} placeholder="1234 High St, Columbus, OH 43201" />
              </div>
              <div className="sm:col-span-2">
                <span className={labelCls}>Is there a gate between the street and the stump? *</span>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[["yes", "Yes — there's a gate"], ["no", "No gate / open access"]].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`quote-gate-${val}`}
                      onClick={() => setForm((f) => ({ ...f, hasGate: val }))}
                      className={`border px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-150 ${
                        form.hasGate === val
                          ? "border-amber-500 bg-amber-500/10 text-amber-500"
                          : "border-stone-700 text-stone-400 hover:border-stone-500 hover:text-stone-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {form.hasGate === "yes" && (
                  <div className="mt-4">
                    <label className={labelCls} htmlFor="q-gate">Tightest gate opening (inches) *</label>
                    <input id="q-gate" data-testid="quote-gate-opening-input" required type="number" min="1" max="240" className={inputCls} value={form.gateOpening} onChange={set("gateOpening")} placeholder="Measure post to post at the narrowest point" />
                    {Number(form.gateOpening) > 0 && Number(form.gateOpening) < 36 && (
                      <p data-testid="gate-tight-warning" className="mt-2 border border-amber-600/50 bg-amber-500/10 px-3 py-2 font-mono text-[11px] leading-relaxed tracking-[0.05em] text-amber-400">
                        Under 36″ — tighter than the grinder usually needs. Send it anyway; Eric confirms access from your photo before Friday.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <span className={labelCls}>Stump widths across the flare roots (in) *</span>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {widths.map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        data-testid={`quote-width-input-${i}`}
                        required
                        type="number"
                        min="1"
                        max="120"
                        className={inputCls}
                        value={w}
                        onChange={setW(i)}
                        onBlur={clampW(i)}
                        aria-label={`Stump ${i + 1} width in inches`}
                      />
                      {i > 0 && (
                        <button
                          type="button"
                          data-testid={`quote-width-remove-${i}`}
                          onClick={() => removeStump(i)}
                          className="shrink-0 border border-stone-700 p-2.5 text-stone-500 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
                          aria-label={`Remove stump ${i + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {widths.length < 6 && (
                  <button
                    type="button"
                    data-testid="quote-add-stump-btn"
                    onClick={addStump}
                    className="mt-2 flex items-center gap-2 border border-dashed border-stone-700 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add another stump
                  </button>
                )}
                <p data-testid="quote-live-estimate" className="mt-2 font-mono text-[11px] tracking-[0.1em] text-amber-500/90">
                  {liveEst
                    ? `${validWidths.length} stump${validWidths.length === 1 ? "" : "s"} · site estimate $${liveEst.low}–$${liveEst.high}`
                    : "Enter stump sizes for an instant estimate"}
                </p>
              </div>
              <div className="sm:col-span-2">
                <span className={labelCls}>Stump photos — up to 4 (wide shot + close-up helps)</span>
                {photos.length > 0 && (
                  <div data-testid="quote-photo-previews" className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {photos.map((p, i) => (
                      <div key={p.id} data-testid={`quote-photo-thumb-${i}`} className="relative border border-stone-700">
                        <img src={p.preview} alt={`Stump upload ${i + 1}`} className="h-20 w-full object-cover" />
                        <button type="button" data-testid={`quote-photo-remove-${i}`} onClick={() => removePhoto(p.id)} className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center border border-stone-600 bg-[#0c0a09]/85 p-1 text-stone-300 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500 sm:h-8 sm:w-8" aria-label={`Remove photo ${i + 1}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < MAX_PHOTOS && (
                  <label data-testid="quote-photo-drop" className="flex min-h-[64px] cursor-pointer items-center justify-center gap-3 border border-dashed border-stone-700 bg-[#0c0a09] px-4 py-6 font-mono text-xs uppercase tracking-[0.15em] text-stone-400 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
                    <ImagePlus className="h-4 w-4" />
                    {photos.length === 0 ? "Attach stump photos" : `Add another (${photos.length}/${MAX_PHOTOS})`}
                    <input data-testid="quote-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={onPhoto} />
                  </label>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="q-notes">Notes / anything else</label>
                <textarea id="q-notes" data-testid="quote-notes-input" rows="3" className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Stump is behind the garage, dog in the yard, sprinkler heads nearby…" />
              </div>
            </div>

            <button
              data-testid="quote-submit-btn"
              type="submit"
              disabled={sending}
              className="group mt-8 flex min-h-[52px] w-full items-center justify-center gap-2 bg-amber-500 px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60 sm:w-auto"
            >
              <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              {sending ? "Grinding…" : "Submit quote request"}
            </button>

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 border-t border-stone-800 pt-6">
              {[
                [ShieldCheck, "100% contactless"],
                [CreditCard, "No card required upfront"],
                [Phone, "Direct to Eric's phone"],
              ].map(([Icon, label]) => (
                <span key={label} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                  <Icon className="h-3.5 w-3.5 text-amber-500" />
                  {label}
                </span>
              ))}
            </div>
          </form>
        )}
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-6 flex flex-col items-start justify-between gap-4 border border-stone-800 bg-[#0a0908] p-6 sm:flex-row sm:items-center">
          <p className="font-mono text-xs leading-relaxed tracking-[0.1em] text-stone-400">
            Hate forms? Same. Skip it —
          </p>
          <div className="flex flex-wrap gap-3">
            <a data-testid="quote-direct-sms" href={`sms:${PHONE_RAW}`} className="flex items-center gap-2 border border-stone-700 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-200 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
              <MessageSquareText className="h-3.5 w-3.5" /> Text {PHONE}
            </a>
            <a data-testid="quote-direct-email" href={`mailto:${EMAIL}`} className="flex items-center gap-2 border border-stone-700 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-200 transition-colors duration-150 hover:border-amber-500 hover:text-amber-500">
              <Mail className="h-3.5 w-3.5" /> {EMAIL}
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
